import { downloadBlob } from "@/lib/downloadBlob";
import type {
	CommandCentreResponse,
	FinanceIntelligenceResponse,
	LabIntelligenceResponse,
	ManagementAttentionResponse,
	PharmacyIntelligenceResponse,
} from "@/api/intelligence";

export function escapeCsvCell(value: string | number | null | undefined): string {
	if (value === null || value === undefined) return "";
	const s = String(value);
	if (s.includes(",") || s.includes('"') || s.includes("\n")) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

export type CsvCell = string | number | null | undefined;
export type CsvRows = CsvCell[][];

export function rowsToCsv(rows: CsvRows): string {
	return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

export function downloadCsv(filename: string, rows: CsvRows) {
	const bom = "\uFEFF";
	const blob = new Blob([bom + rowsToCsv(rows)], { type: "text/csv;charset=utf-8;" });
	downloadBlob(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

function metaRows(scopeLabel: string, periodLabel: string): CsvRows {
	return [
		["THMS Intelligence Export"],
		["Period", periodLabel],
		["Scope", scopeLabel],
		["Exported at", new Date().toISOString()],
		[],
	];
}

export function buildCommandCentreExportRows(data: CommandCentreResponse): CsvRows {
	const rows = metaRows(data.scope.hospitalName ?? "Consolidated", data.period.label);
	rows.push(["Executive KPIs"]);
	rows.push([
		"Key",
		"Name",
		"Value",
		"Previous",
		"Change %",
		"Availability",
		"Limitations",
	]);
	for (const k of data.kpis) {
		rows.push([
			k.key,
			k.displayName,
			k.value,
			k.previousValue ?? "",
			k.percentChange ?? "",
			k.dataAvailability,
			k.limitations ?? "",
		]);
	}
	rows.push([]);
	rows.push(["Domain summaries"]);
	for (const summary of data.domainSummaries) {
		rows.push([summary.label]);
		rows.push(["Metric", "Value", "Previous", "Change %"]);
		for (const m of summary.metrics) {
			rows.push([m.label, m.value, m.previousValue ?? "", m.percentChange ?? ""]);
		}
		rows.push([]);
	}
	if (data.managementAttention.items.length) {
		rows.push(["Management attention"]);
		rows.push(["Severity", "Title", "Metric", "Current", "Previous", "Change %", "Action"]);
		for (const item of data.managementAttention.items) {
			rows.push([
				item.severity,
				item.title,
				item.metricKey,
				item.currentValue ?? "",
				item.previousValue ?? "",
				item.changePercent ?? "",
				item.recommendedAction ?? "",
			]);
		}
	}
	return rows;
}

export function buildPharmacyExportRows(data: PharmacyIntelligenceResponse): CsvRows {
	const rows = metaRows(
		data.scope.hospitalId ?? "Consolidated",
		data.period.label
	);
	rows.push(["Metrics"]);
	rows.push(["Key", "Label", "Value", "Previous", "Change %"]);
	for (const m of data.metrics) {
		rows.push([m.key, m.label, m.value, m.previousValue ?? "", m.percentChange ?? ""]);
	}
	rows.push([]);
	rows.push(["Daily trend"]);
	rows.push(["Date", "Revenue", "Issue value"]);
	for (const p of data.dailyTrend) {
		rows.push([p.date, p.revenue, p.issueValue]);
	}
	if (data.topDrugs.length) {
		rows.push([]);
		rows.push(["Top drugs"]);
		rows.push(["Drug", "Qty", "MRP value", "Cost value"]);
		for (const d of data.topDrugs) {
			rows.push([d.medicineName, d.totalQty, d.mrpValue, d.costValue]);
		}
	}
	if (data.hospitalComparison.enabled) {
		rows.push([]);
		rows.push(["Hospital comparison"]);
		rows.push(["Hospital", "Revenue", "Issue value", "Stock value", "Near expiry", "Slow moving"]);
		for (const h of data.hospitalComparison.rows) {
			rows.push([
				h.hospitalName,
				h.revenue,
				h.issueValue,
				h.stockValue,
				h.nearExpiryValue,
				h.slowMovingValue,
			]);
		}
	}
	return rows;
}

export function buildLabExportRows(data: LabIntelligenceResponse): CsvRows {
	const rows = metaRows(data.scope.hospitalId ?? "Consolidated", data.period.label);
	rows.push(["Metrics"]);
	rows.push(["Key", "Label", "Value", "Previous", "Change %"]);
	for (const m of data.metrics) {
		rows.push([m.key, m.label, m.value, m.previousValue ?? "", m.percentChange ?? ""]);
	}
	rows.push([]);
	rows.push(["Daily trend"]);
	rows.push(["Date", "Revenue", "Test volume"]);
	for (const p of data.dailyTrend) {
		rows.push([p.date, p.revenue, p.testVolume]);
	}
	if (data.topTests.length) {
		rows.push([]);
		rows.push(["Top tests"]);
		rows.push(["Test", "Volume", "Revenue"]);
		for (const t of data.topTests) {
			rows.push([t.testName, t.volume, t.revenue]);
		}
	}
	if (data.hospitalComparison.enabled) {
		rows.push([]);
		rows.push(["Hospital comparison"]);
		rows.push(["Hospital", "Revenue", "Tests", "Pending", "Avg TAT hrs"]);
		for (const h of data.hospitalComparison.rows) {
			rows.push([
				h.hospitalName,
				h.revenue,
				h.testVolume,
				h.pendingTests,
				h.averageTatHours ?? "",
			]);
		}
	}
	return rows;
}

export function buildFinanceExportRows(data: FinanceIntelligenceResponse): CsvRows {
	const rows = metaRows(data.scope.hospitalId ?? "Consolidated", data.period.label);
	rows.push(["Core KPIs"]);
	rows.push(["Key", "Name", "Value", "Previous", "Change %"]);
	for (const k of data.coreKpis) {
		rows.push([k.key, k.name, k.value, k.previousValue ?? "", k.percentChange ?? ""]);
	}
	rows.push([]);
	rows.push(["Finance metrics"]);
	rows.push(["Key", "Label", "Value", "Previous", "Change %"]);
	for (const m of data.metrics) {
		rows.push([m.key, m.label, m.value, m.previousValue ?? "", m.percentChange ?? ""]);
	}
	rows.push([]);
	rows.push(["Receivables aging", data.receivablesAging.snapshotAsOf]);
	rows.push(["Bucket", "Amount", "Share %"]);
	for (const b of data.receivablesAging.buckets) {
		rows.push([b.label, b.amount, b.percent]);
	}
	rows.push([]);
	rows.push(["One-time expenses"]);
	rows.push(["Capex", data.oneTimeExpenses?.capex ?? 0]);
	rows.push(["Funding", data.oneTimeExpenses?.funding ?? 0]);
	rows.push(["Total", data.oneTimeExpenses?.total ?? 0]);
	rows.push([]);
	rows.push(["Daily trend"]);
	rows.push(["Date", "Net revenue", "Collections", "Outstanding"]);
	for (const p of data.dailyTrend) {
		rows.push([p.date, p.netRevenue, p.collections, p.outstanding]);
	}
	if (data.revenueCollectionMom?.length) {
		rows.push([]);
		rows.push(["Revenue vs collection MoM (cohort till date)"]);
		rows.push([
			"Month",
			"Net revenue",
			"Collected against month revenue",
			"Still open",
			"Eff %",
		]);
		for (const r of data.revenueCollectionMom) {
			rows.push([
				r.label,
				r.netRevenue,
				r.collectedAgainstRevenue,
				r.outstandingAgainstRevenue,
				r.collectionEfficiency,
			]);
		}
	}
	if (data.hospitalComparison.enabled) {
		rows.push([]);
		rows.push(["Hospital comparison"]);
		rows.push(["Hospital", "Net revenue", "Collections", "Outstanding", "Collection eff %"]);
		for (const h of data.hospitalComparison.rows) {
			rows.push([
				h.hospitalName,
				h.netRevenue,
				h.collections,
				h.outstanding,
				h.collectionEfficiency,
			]);
		}
	}
	return rows;
}

export function buildAttentionExportRows(data: ManagementAttentionResponse, periodLabel: string): CsvRows {
	const rows = metaRows("See filters", periodLabel);
	rows.push(["Management attention items"]);
	rows.push([
		"Severity",
		"Category",
		"Title",
		"Description",
		"Metric",
		"Current",
		"Previous",
		"Change %",
		"Threshold",
		"Recommended action",
	]);
	for (const item of data.items) {
		rows.push([
			item.severity,
			item.category,
			item.title,
			item.description,
			item.metricKey,
			item.currentValue ?? "",
			item.previousValue ?? "",
			item.changePercent ?? "",
			item.thresholdLabel,
			item.recommendedAction ?? "",
		]);
	}
	return rows;
}
